import React, { useState, useEffect, useCallback } from 'react';
import { sessionsAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { MonitorSmartphone, RefreshCw, XCircle } from 'lucide-react';

export default function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [terminateTarget, setTerminateTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState('');

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await sessionsAPI.list();
      if (res.ok) setSessions(await res.clone().json());
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleTerminate = async () => {
    if (!terminateTarget) return;
    setActionLoading(terminateTarget.id);
    try {
      const res = await sessionsAPI.terminate(terminateTarget.id);
      if (res.ok) { toast.success('Sessie beëindigd'); loadSessions(); }
      else { const d = await res.clone().json(); toast.error(d.detail || 'Fout'); }
    } catch { toast.error('Fout bij beëindigen'); }
    setActionLoading('');
    setTerminateTarget(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('nl-NL', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#1a2332]">Actieve sessies</h3>
        <Button variant="outline" size="sm" onClick={loadSessions} className="rounded-xl">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Vernieuwen
        </Button>
      </div>

      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12">
              <MonitorSmartphone className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Geen actieve sessies</p>
            </div>
          ) : (
            <Table data-testid="sessions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Gebruiker</TableHead>
                  <TableHead className="hidden sm:table-cell">Apparaat</TableHead>
                  <TableHead className="hidden md:table-cell">IP-adres</TableHead>
                  <TableHead>Laatst actief</TableHead>
                  <TableHead className="text-right">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map(s => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <p className="font-medium text-sm">{s.user_naam || 'Onbekend'}</p>
                      <p className="text-xs text-slate-500">{s.user_email || ''}</p>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <span className="text-sm text-slate-600">{(s.device || 'Onbekend').substring(0, 40)}</span>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <code className="text-xs text-slate-500">{s.ip_address || '-'}</code>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm tabular-nums text-slate-600">{formatDate(s.last_used)}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setTerminateTarget(s)}
                        disabled={actionLoading === s.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        data-testid="sessions-terminate-button"
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Beëindigen
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!terminateTarget} onOpenChange={() => setTerminateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sessie beëindigen?</AlertDialogTitle>
            <AlertDialogDescription>
              De sessie van {terminateTarget?.user_naam || 'deze gebruiker'} wordt beëindigd.
              De gebruiker wordt uitgelogd.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleTerminate} className="bg-red-600 hover:bg-red-700">
              Beëindigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
